import {
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleReelModelSymbolPosition
} from "../../core/reel/simplereel/SimpleReelModel";
import {DasUBootSymbol, DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {DasUBoot} from "../DasUBoot";
import {gsap} from "gsap";
import {BombSymbol} from "../reels/symbols/BombSymbol";
import {DasUBootReel} from "../reels/DasUBootReel";
import {ScatterSymbol} from "../reels/symbols/ScatterSymbol";
import {MysterySymbol} from "../reels/symbols/MysterySymbol";
import {PeriscopeSymbol} from "../reels/symbols/PeriscopeSymbol";
import {DasUBootSound} from "../sound/DasUBootSound";
import {StackedWildSymbol} from "../reels/symbols/StackedWildSymbol";
import {ProxySymbol} from "../reels/symbols/ProxySymbol";
import {TorpedoSymbol} from "../reels/symbols/TorpedoSymbol";
import {ClientXNudge} from "../server/DasUBootData";
import {StateMatrixCoordinate} from "./StateMatrix";

/**
 * Created by jonas on 2021-02-19.
 */

export class SymbolUtils {
    public static isProxyWild(value:string) {
        return value == "W1" || value == "W2" || value == "W3"
    }

    public static createSymbol(reel:DasUBootReel, symbolName:string, state:SimpleReelModelState, symbolPosition?:SimpleReelModelSymbolPosition):DasUBootSymbol {

        let symbolState = DasUBootSymbolStates.SPIN;
        if (state == SimpleReelModelState.IDLE) {
            symbolState = DasUBootSymbolStates.NORMAL;
        }

        if (symbolName == "B" || symbolName == "BB") {
            return new BombSymbol(reel, symbolName).init(symbolState);
        }

        if (symbolName == "S") {
            return new ScatterSymbol(reel, symbolName).init(symbolState);
        }

        if (symbolName == "M" || symbolName == "MW") {
            return new MysterySymbol(reel, symbolName).init(symbolState);
        }

        if (symbolName == "P") {
            return new PeriscopeSymbol(reel, symbolName).init(symbolState);
        }

        if (symbolName == "PROXY" || symbolName == "X") {
            return new ProxySymbol(reel, symbolName).init(symbolState);
        }

        if (symbolName == "W4") {
            return new StackedWildSymbol(reel, symbolName).init(symbolState);
        }


        if (symbolName == "T") {
            return new TorpedoSymbol(reel, symbolName).init(symbolState);
        }

        if (SymbolUtils.isProxyWild(symbolName)) {
            return new ProxySymbol(reel, symbolName).init(symbolState);
        }

        return new DasUBootSymbol(reel, symbolName).init(symbolState);
    }

    public static getAllSymbolsWithState(state:DasUBootSymbolStates):SimpleReelModelSymbolData[] {
        const coords = DasUBoot.model.clientData.winData.matrix.getAllCoordinateForValue(state);
        const symbols = [];
        for (let coordinate of coords) {
            symbols.push(DasUBoot.reelView.getVisibleSymbolAt(coordinate.column, coordinate.row));
        }
        return symbols;
    }

    public static getAllSymbolsWithStateByReel(state:DasUBootSymbolStates):SimpleReelModelSymbolData[][] {
        const coords = DasUBoot.model.clientData.winData.matrix.getAllCoordinateForValue(state);
        const reel:SimpleReelModelSymbolData[][] = [[], [], [], [], [], []];
        for (let coordinate of coords) {
            reel[coordinate.column].push(DasUBoot.reelView.getVisibleSymbolAt(coordinate.column, coordinate.row));
        }
        return reel;
    }

    public static getSymbolStateAnimations(symbols:SimpleReelModelSymbolData[], state:DasUBootSymbolStates):gsap.core.Timeline[] {
        const result:gsap.core.Timeline[] = [];
        for (let sym of symbols) {
            result.push((<DasUBootSymbol>sym.positionable).getStateAnimation(state));
        }
        return result;
    }

    public static getSymbolShakeAnimations(symbols:SimpleReelModelSymbolData[], duration:number):gsap.core.Timeline {
        const result:gsap.core.Timeline = gsap.timeline();
        for (let sym of symbols) {
            result.add((<DasUBootSymbol>sym.positionable).getShakeAnimation(duration), 0);
        }
        return result;
    }

    public static getPeriscopeNudgeAnimation(reel:number, steps:number, reverse:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        for (let i:number = 0; i < steps; i++) {

            const nudgeTL = gsap.timeline();

            nudgeTL.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.PERISCOPE_WILD_NUDGE));

            const windUp = DasUBoot.reelView.reels[reel].model.windUp(0.3, 2.5, reverse);
            nudgeTL.add(windUp);

            const nudge = DasUBoot.reelView.reels[reel].model.nudge(reverse);
            nudge.duration(0.1);
            nudgeTL.add(nudge);

            nudgeTL.add(() => {DasUBoot.reelView.getLandingShake(reverse).play(0)});

            tl.add([
                nudgeTL,
                gsap.to(this, {duration:0.5}),
            ]);

            tl.add([
                DasUBoot.reelView.reelFrame.reelLamps[reel].incrementValue(1, steps + 1),
            ], "-=0.1");
        }

        return tl;
    }


    public static getXNudgeAnimation(data:ClientXNudge, symbol:StackedWildSymbol, fastNudge:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        const reel = data.reel
        const steps = data.steps;
        const reverse = data.up;
        const isWin = data.isInWin;

        if (steps <= 0) {
            return tl;
        }

        if (fastNudge) {
            const nudgeTL = gsap.timeline();
            nudgeTL.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.WILD_NUDGE));

            const windUp = DasUBoot.reelView.reels[reel].model.windUp(0.3, steps == 0 ? 3.3 : 2.5, reverse);
            nudgeTL.add(windUp);

            const stepDur = 0.1 / steps;
            for (let i:number = 0; i < steps; i++) {
                const nudge = DasUBoot.reelView.reels[reel].model.nudge(reverse);
                nudge.duration(stepDur);
                nudgeTL.add(nudge);
            }

            nudgeTL.add(() => {DasUBoot.reelView.getLandingShake(reverse).play(0)});

            tl.add([
                nudgeTL,
                gsap.to(this, {duration:0.5}),
            ]);

            //if (isWin) {
            tl.add([
                DasUBoot.reelView.reelFrame.reelLamps[reel].setValue(steps + 1, true, steps + 1),
            ]);
            //}


        } else {
            for (let i:number = 0; i < steps; i++) {

                const nudgeTL = gsap.timeline();

                nudgeTL.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.WILD_NUDGE));

                const windUp = DasUBoot.reelView.reels[reel].model.windUp(0.3, 2.5, reverse);
                nudgeTL.add(windUp);

                const nudge = DasUBoot.reelView.reels[reel].model.nudge(reverse);
                nudge.duration(0.1);
                nudgeTL.add(nudge);

                nudgeTL.add(() => {DasUBoot.reelView.getLandingShake(reverse).play(0)});

                tl.add([
                    nudgeTL,
                    gsap.to(this, {duration:0.5}),
                ]);

                tl.add([
                    DasUBoot.reelView.reelFrame.reelLamps[reel].incrementValue(1, steps + 1),
                ], "-=0.1");
            }
        }


        return tl;
    }

    public static createSubSymbols(reel:number, count:number, symbolName:string):DasUBootSymbol[] {
        const scale = 1 / count;
        const partSize = 144 / count;
        const symbols:DasUBootSymbol[] = [];
        for (let i:number = 0; i < count; i++) {
            const symbol = SymbolUtils.createSymbol(DasUBoot.reelView.reels[reel], symbolName, SimpleReelModelState.IDLE, {
                reelID:reel,
                position:i
            });
            symbol.resize(scale, partSize * i, 0).progress(1);
            symbols.push(symbol);
        }
        return symbols;
    }


    public static removeExplodedSymbols(positions:StateMatrixCoordinate[]) {
        ///remove symbols from reel.
        for (let i:number = positions.length - 1; i >= 0; i--) {
            const pos = positions[i];
            DasUBoot.reelView.reels[pos.column].removeAvalancheSymbol(pos.row);
        }
    }


}